// Live GHIN handicap lookup — ported from
// /Users/mratliff/PycharmProjects/GolfGamesScorecard/utils/ghinConfig.ts.
//
// Two-call flow per lookup:
//   1) POST /api/v1/golfer_login   → JSON, returns a JWT.
//      Body must include a per-request UUID under `token`.
//   2) GET  /api/v1/golfers?golfer_id=…&global_search=true&search=…
//      Authorization: Bearer <JWT>. Response body is XML even though we
//      send Accept: application/json — that's the actual API behaviour.
//      We extract <handicap-index> from the XML.
//
// The JWT lives in a module-level cache for the page's lifetime; a 401
// on the search call triggers a single re-login + retry.
//
// Firebase cache (/golferCache/<ghin>): handicaps don't change much, so
// after the first successful lookup we persist { firstName, lastName,
// handicapIndex, ghinNumber, lastUpdated } and reuse it for ≤ 6 weeks
// before going back to api.ghin.com. Stored fields are name + index +
// GHIN only — no email or other PII.

import { ref as dbRef, get as dbGet, set as dbSet } from 'firebase/database'
import { db } from './firebase.js'

export const GHIN_USERNAME = '11765966'
export const GHIN_PASSWORD = 'Provider#1'

const BASE = 'https://api.ghin.com/api/v1'
const CACHE_TTL_MS = 6 * 7 * 24 * 60 * 60 * 1000  // 6 weeks

let _token = null

async function readCachedGolfer(ghin) {
  try {
    const snap = await dbGet(dbRef(db, `golferCache/${ghin}`))
    return snap.exists() ? snap.val() : null
  } catch {
    return null
  }
}

async function writeCachedGolfer(ghin, firstName, lastName, handicapIndex) {
  try {
    await dbSet(dbRef(db, `golferCache/${ghin}`), {
      firstName: firstName || '',
      lastName: lastName || '',
      handicapIndex,
      ghinNumber: ghin,
      lastUpdated: Date.now(),
    })
  } catch {
    /* cache write failures must never break the lookup */
  }
}

// RFC4122 v4 UUID generator, inline so no packages are needed. Matches
// the snippet provided in the spec exactly so bit layout + length are
// guaranteed.
function uuidv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

async function login() {
  try {
    const r = await fetch(`${BASE}/golfer_login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        user: {
          email_or_ghin: GHIN_USERNAME,
          password: GHIN_PASSWORD,
          remember_me: true,
        },
        token: uuidv4(),
      }),
    })
    if (!r.ok) return null
    const j = await r.json()
    return j?.golfer_user?.golfer_user_token ?? null
  } catch {
    return null
  }
}

async function getToken(force = false) {
  if (!force && _token) return _token
  _token = await login()
  return _token
}

// Pull <handicap-index>VALUE</handicap-index> out of GHIN's XML
// response. Uses DOMParser when the runtime exposes it (every
// browser); falls back to a tight regex otherwise. Both paths look for
// the same node name, so swap in any XML parser later without touching
// call sites.
function parseHandicapIndex(xml) {
  try {
    const DP = typeof DOMParser !== 'undefined' ? DOMParser : null
    if (DP) {
      const doc = new DP().parseFromString(xml, 'text/xml')
      const el = doc.getElementsByTagName('handicap-index')[0]
      const txt = el?.textContent
      if (txt) {
        const n = Number(txt)
        if (Number.isFinite(n)) return n
      }
    }
  } catch {
    /* fall through to regex */
  }
  const m = xml.match(/<handicap-index[^>]*>([^<]+)<\/handicap-index>/)
  if (m) {
    const n = Number(m[1])
    if (Number.isFinite(n)) return n
  }
  return null
}

// Pull a single named text node out of GHIN's XML response. Used for
// <first-name>, <last-name>, and any other simple-string field. Same
// DOMParser-then-regex contract as parseHandicapIndex so the two stay
// in sync. Returns '' (not null) so callers can do plain concatenation
// without nullish checks.
function parseTextTag(xml, tag) {
  try {
    const DP = typeof DOMParser !== 'undefined' ? DOMParser : null
    if (DP) {
      const doc = new DP().parseFromString(xml, 'text/xml')
      const el = doc.getElementsByTagName(tag)[0]
      const txt = (el?.textContent || '').trim()
      if (txt) return txt
    }
  } catch {
    /* fall through to regex */
  }
  // Escape `-` in the tag for the regex source. The tags we care about
  // (first-name, last-name) contain hyphens that need no escaping for
  // RegExp but we keep the helper general for any future field.
  const safe = String(tag).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const m = xml.match(new RegExp(`<${safe}[^>]*>([^<]+)</${safe}>`))
  return m ? m[1].trim() : ''
}

/**
 * Look up a golfer's official handicap index by GHIN number.
 * Returns { ok: true, handicapIndex, firstName, lastName, fullName } on
 * success; { ok: false, reason } on miss / auth failure / network
 * error. The UI treats any non-ok response as "GHIN not found".
 *
 * Return type (extended from the GolfGamesScorecard reference to also
 * expose the golfer's name from the same /golfers response):
 *   { ok: true,  handicapIndex: number, firstName: string,
 *                lastName: string, fullName: string }
 *   { ok: false, reason: 'notfound' | 'auth' | 'network' }
 *
 * `fullName` is `"First Last"` (trimmed, single space) for the common
 * case where callers want a single string to drop into a name input.
 */
export async function lookupGhinHandicap(ghin) {
  const clean = (ghin || '').replace(/\D/g, '')
  if (!clean) return { ok: false, reason: 'notfound' }

  // Cache hit < 6 weeks old → skip the API entirely.
  const cached = await readCachedGolfer(clean)
  if (cached && cached.handicapIndex != null
      && Date.now() - (cached.lastUpdated || 0) < CACHE_TTL_MS) {
    const fullName = [cached.firstName, cached.lastName].filter(Boolean).join(' ')
    return {
      ok: true,
      handicapIndex: cached.handicapIndex,
      firstName: cached.firstName,
      lastName: cached.lastName,
      fullName,
      cached: true,
    }
  }

  try {
    let token = await getToken()
    if (!token) return { ok: false, reason: 'auth' }
    const url =
      `${BASE}/golfers?golfer_id=${encodeURIComponent(clean)}` +
      `&page=1&per_page=1&global_search=true&search=${encodeURIComponent(clean)}`
    const buildHeaders = (t) => ({
      Authorization: `Bearer ${t}`,
      Accept: 'application/json',
    })
    let r = await fetch(url, { headers: buildHeaders(token) })
    if (r.status === 401) {
      // Cached JWT went stale — re-login once and retry.
      token = await getToken(true)
      if (!token) return { ok: false, reason: 'auth' }
      r = await fetch(url, { headers: buildHeaders(token) })
    }
    if (!r.ok) return { ok: false, reason: 'network' }
    const xml = await r.text()
    const hi = parseHandicapIndex(xml)
    if (hi == null) return { ok: false, reason: 'notfound' }
    const firstName = parseTextTag(xml, 'first-name')
    const lastName  = parseTextTag(xml, 'last-name')
    const fullName  = [firstName, lastName].filter(Boolean).join(' ')
    // Fire-and-forget cache write — UI returns immediately while RTDB
    // persists in the background. Covers both first-time inserts and
    // refreshes of expired records.
    writeCachedGolfer(clean, firstName, lastName, hi)
    return { ok: true, handicapIndex: hi, firstName, lastName, fullName }
  } catch {
    return { ok: false, reason: 'network' }
  }
}

